'use client';

import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import ScheduleModal from '@/components/ScheduleModal';
import MobileCTASection from './MobileCTASection';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const CTASection = () => {
    const containerRef = useRef<HTMLElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [isMobile, setIsMobile] = React.useState(false);

    // Detect Mobile
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // GSAP Logic (Desktop Only)
    useEffect(() => {
        if (isMobile) return;

        const ctx = gsap.context(() => {
            // Parallax Background Shapes
            gsap.to(".bg-shape", {
                yPercent: -20,
                ease: "none",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: 1
                }
            });

            // Content Reveal
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top 70%",
                    toggleActions: "play none none reverse"
                }
            });

            tl.from(".cta-badge", { y: 20, opacity: 0, duration: 0.6, ease: "power2.out" })
                .from(".cta-heading", { y: 40, opacity: 0, duration: 0.8, ease: "power3.out" }, "-=0.4")
                .from(".cta-desc", { y: 20, opacity: 0, duration: 0.8, ease: "power2.out" }, "-=0.6")
                .from(".cta-feature", { x: -20, opacity: 0, duration: 0.8, stagger: 0.1, ease: "power2.out" }, "-=0.4")
                .from(".cta-actions", { y: 20, opacity: 0, duration: 0.6, ease: "back.out(1.7)" }, "-=0.4")
                .from(".cta-trust", { opacity: 0, duration: 0.8 }, "-=0.2");

            // Magnetic Button Effect
            const btn = btnRef.current;
            if (btn) {
                btn.addEventListener('mousemove', (e) => {
                    const rect = btn.getBoundingClientRect();
                    const x = e.clientX - rect.left - rect.width / 2;
                    const y = e.clientY - rect.top - rect.height / 2;
                    gsap.to(btn, { x: x * 0.3, y: y * 0.3, duration: 0.3, ease: "power2.out" });
                });
                btn.addEventListener('mouseleave', () => {
                    gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.3)" });
                });
            }

        }, containerRef);
        return () => ctx.revert();
    }, [isMobile]);

    const features = [
        "Free project consultation",
        "Custom strategy roadmap",
        "No-obligation proposal",
        "Quick 24-hour response"
    ];

    // Modal State
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    if (isMobile) {
        return <MobileCTASection />;
    }

    return (
        <section id="cta-section" ref={containerRef} className="py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-indigo-100/40 to-blue-50/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 bg-shape" />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-50/40 to-indigo-100/40 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 bg-shape" />

            <div className="container mx-auto px-6 relative z-10">
                <div ref={contentRef} className="max-w-4xl mx-auto text-center">

                    {/* Badge */}
                    <div className="cta-badge inline-flex items-center space-x-2 px-3 py-1 bg-white border border-indigo-100 rounded-full mb-8 shadow-sm">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                        </span>
                        <span className="text-sm font-medium text-slate-600">Start Your Journey</span>
                    </div>

                    {/* Headline */}
                    <h2 className="cta-heading text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-8 tracking-tight leading-tight">
                        Ready to Elevate Your <br className="hidden md:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">Digital Presence?</span>
                    </h2>

                    {/* Description */}
                    <p className="cta-desc text-lg md:text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed">
                        Let's transform your vision into a powerful digital ecosystem that drives measurable growth and brand visibility.
                    </p>

                    {/* Features Grid */}
                    <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12 text-left">
                        {features.map((item, i) => (
                            <div key={i} className="cta-feature flex items-center space-x-3 p-4 rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                                    <Icon name="CheckCircleIcon" className="w-5 h-5 text-indigo-600" />
                                </div>
                                <span className="font-medium text-slate-700">{item}</span>
                            </div>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="cta-actions flex flex-col sm:flex-row items-center justify-center gap-6 mb-16">
                        <button
                            ref={btnRef}
                            onClick={() => setIsModalOpen(true)}
                            className="group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white bg-slate-900 rounded-full overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                        >
                            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-indigo-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <span className="relative flex items-center">
                                Start Your Project
                                <Icon name="ArrowRightIcon" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </button>

                        <a
                            href="/homepage#ecosystem-showcase"
                            onClick={(e) => {
                                e.preventDefault();
                                const element = document.getElementById('ecosystem-showcase');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                    window.location.href = '/homepage#ecosystem-showcase';
                                }
                            }}
                            className="inline-flex items-center text-lg font-semibold text-slate-600 hover:text-indigo-600 transition-colors"
                        >
                            View Our Work
                            <Icon name="ArrowTopRightOnSquareIcon" className="ml-2 w-5 h-5" />
                        </a>
                    </div>

                    {/* Trust Signals */}
                    <div className="cta-trust pt-8 border-t border-slate-200">
                        <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="flex items-center space-x-1">
                                {[...Array(5)].map((_, i) => (
                                    <Icon key={i} name="StarIcon" className="w-5 h-5 text-yellow-400 fill-current" />
                                ))}
                            </div>
                            <p className="text-slate-500 font-medium">
                                <span className="text-slate-900 font-bold">4.9/5 Average Rating</span> • Trusted by 25+ businesses across India, USA & Canada
                            </p>
                        </div>
                    </div>

                </div>
            </div>

            {/* SCHEDULE MODAL */}
            <ScheduleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </section>
    );
};

export default CTASection;
